import type { CatalogPolicy, CatalogRequestType } from "@/lib/catalog-api";
import type { DraftPolicy } from "../../ConfigurationApp";

export function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function emptyPolicy(): DraftPolicy {
  return {
    code: "DEFAULT_INROOM",
    displayName: "Default in-room",
    isActive: true,
    tiers: [
      {
        tierOrder: 1,
        roleCode: "PCT",
        timeoutSeconds: 90,
        displayName: "Patient Care Technician",
        channels: [],
      },
      {
        tierOrder: 2,
        roleCode: "NURSE",
        timeoutSeconds: 90,
        displayName: "Nurse",
        channels: [],
      },
      {
        tierOrder: 3,
        roleCode: "CHARGE_NURSE",
        timeoutSeconds: 90,
        displayName: "Charge Nurse",
        channels: [],
      },
    ],
  };
}

export function policyToDraft(policy: CatalogPolicy): DraftPolicy {
  return {
    escalationPolicyId: policy.escalationPolicyId,
    code: policy.code,
    displayName: policy.displayName,
    isActive: policy.isActive,
    tiers: policy.tiers.map((tier) => ({
      escalationTierId: tier.escalationTierId,
      tierOrder: tier.tierOrder,
      roleCode: tier.roleCode,
      timeoutSeconds: tier.timeoutSeconds,
      displayName: tier.displayName,
      channels: tier.channels.map((channel) => ({
        escalationTierChannelId: channel.escalationTierChannelId,
        notificationChannelId: channel.notificationChannelId,
        sortOrder: channel.sortOrder,
        isRequired: channel.isRequired,
        templateKey: channel.templateKey,
        destinationRuleJson: channel.destinationRuleJson,
        channelCode: channel.channelCode,
      })),
    })),
  };
}

export function toPolicyCode(name: string): string {
  return name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 64);
}

export function policySignature(draft: DraftPolicy): string {
  return JSON.stringify({
    code: draft.code,
    displayName: draft.displayName,
    isActive: draft.isActive,
    tiers: draft.tiers.map((tier) => ({
      escalationTierId: tier.escalationTierId ?? null,
      tierOrder: tier.tierOrder,
      roleCode: tier.roleCode,
      timeoutSeconds: tier.timeoutSeconds,
      displayName: tier.displayName,
      channels: tier.channels.map((channel) => ({
        escalationTierChannelId: channel.escalationTierChannelId ?? null,
        notificationChannelId: channel.notificationChannelId,
        sortOrder: channel.sortOrder,
        isRequired: channel.isRequired,
        templateKey: channel.templateKey ?? null,
        destinationRuleJson: channel.destinationRuleJson ?? null,
      })),
    })),
  });
}

export function requestSignature(types: CatalogRequestType[]): string {
  return JSON.stringify(
    types.map((item) => ({
      requestTypeId: item.requestTypeId,
      code: item.code,
      title: item.title,
      confirmationTemplate: item.confirmationTemplate,
      isPatientSelectable: item.isPatientSelectable,
      isEnabled: item.isEnabled,
      sortOrder: item.sortOrder,
      escalationPolicyId: item.escalationPolicyId ?? null,
      iconKey: item.iconKey ?? null,
      priority: item.priority ?? "Routine",
    })),
  );
}

export type DefaultsSnapshot = {
  wakePhrase: string;
  wakeOnnx: string;
  voiceAgentId: string;
  webhookSecret: string;
  updateWebhook: boolean;
  defaultPolicyId: number | "";
  configEnabled: boolean;
};

export function defaultsSignature(snapshot: DefaultsSnapshot): string {
  return JSON.stringify(snapshot);
}

export function preferredPolicy(
  policies: CatalogPolicy[],
): CatalogPolicy | undefined {
  return (
    policies.find((policy) => policy.code === "DEFAULT_INROOM") ??
    policies.find((policy) => policy.isActive) ??
    policies[0]
  );
}
